import * as React from 'react';
import { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import { SEO } from '@/components/SEO';
import { ProductMetadata } from '@/components/ProductMetadata';
import { Card, CardContent } from '@/components/ui/card';
import { FeatureCardSimple } from '@/components/ui/feature-card-simple';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { getProductByName } from '@/data/productReference';
import { ChevronUp, Download, Phone, Mail, Droplets, Lightbulb, Thermometer, Timer, Wifi, Shield, Sparkles, User } from 'lucide-react';
import ImageLightbox from '@/components/ui/image-lightbox';
import { ProductGallery } from '@/components/ui/product-gallery';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import OptimizedVideo from '@/components/ui/optimized-video';
import PDFDownloadButton from '@/components/ui/pdf-download-button';
import { RelatedProducts } from '@/components/RelatedProducts';
import { Footer } from '@/components/Footer';
import { ShippingTimeInfo } from '@/components/ShippingTimeInfo';

// Import product images
import aureliusMainImage from '@/assets/aurelius-series-product-main.jpg';
import aureliusSideImage from '@/assets/aurelux-series-product-side.jpg';
import aureliusDetailImage from '@/assets/aurelux-series-product-detail.jpg';
import aureliusLifestyleImage from '@/assets/aurelius-series-lifestyle.jpg';

import { PriceFrom } from "@/components/products/PriceFrom";
const Aurelius = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop(); // Scroll to top when page loads

  const product = getProductByName("AURELIUS");

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleDownload = (filename: string, displayName: string) => {
    const link = document.createElement('a');
    link.href = `/files/${filename}`;
    link.download = displayName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Product image gallery data
  const productImages = [
    {
      src: aureliusMainImage,
      alt: "AURELUX M132 luxury smart toilet with premium bidet system and instant heating technology",
      title: "Premium Design"
    },
    {
      src: aureliusSideImage,
      alt: "AURELUX M132 luxury smart toilet side profile showing elegant design and smart controls",
      title: "Sleek Profile"
    },
    {
      src: aureliusDetailImage,
      alt: "AURELUX M132 luxury smart toilet wireless remote control panel with advanced features",
      title: "Smart Controls"
    },
    {
      src: aureliusLifestyleImage,
      alt: "AURELUX M132 luxury smart toilet installed in premium bathroom setting",
      title: "Lifestyle Setting"
    }
  ];

  // Premium features with icons and descriptions
  const premiumFeatures = [
    {
      icon: User,
      title: "User Memory System",
      description: "Personalized settings for multiple users with memory recall functionality"
    },
    {
      icon: Droplets,
      title: "Advanced Bidet Functions",
      description: "Multiple spray patterns with adjustable pressure and temperature control"
    },
    {
      icon: Lightbulb,
      title: "LED Night Light",
      description: "Soft ambient lighting for nighttime use with motion activation"
    },
    {
      icon: Thermometer,
      title: "Heated Seat & Water",
      description: "Instant heating technology with temperature memory settings"
    },
    {
      icon: Timer,
      title: "Auto Functions",
      description: "Automatic lid opening/closing, flush, and seat positioning"
    },
    {
      icon: Wifi,
      title: "Smart Connectivity",
      description: "Wireless remote control and smart home integration capability"
    }
  ];

  // Complete feature list
  const keyFeatures = [
    "Wider comfort seat design for enhanced user experience",
    "Dual voltage compatibility (110V/60Hz & 220V/50Hz)",
    "1100W high-efficiency power system",
    "User memory settings for personalized experience",
    "Instant water heating technology",
    "Multi-pattern bidet spray functions",
    "Automatic lid and seat operation",
    "LED night light with motion sensor",
    "Heated seat with temperature control",
    "Self-cleaning nozzle system",
    "Wireless remote control",
    "Energy-saving eco mode",
    "Soft-close seat and lid",
    "Premium ceramic construction",
    "Advanced hygiene features"
  ];

  // Technical specifications
  const specifications = [
    { label: "Overall Dimensions", value: "710 × 410 × 450 mm" },
    { label: "Voltage/Frequency", value: "110V/60Hz & 220V/50Hz" },
    { label: "Power Consumption", value: "1100W" },
    { label: "Water Temperature", value: "32-40°C (89-104°F)" },
    { label: "Seat Temperature", value: "32-40°C (89-104°F)" },
    { label: "Water Pressure", value: "0.08-0.75 MPa" },
    { label: "Rough-in", value: "300mm (adjustable 200-450mm)" },
    { label: "Installation Type", value: "Floor-mounted" },
    { label: "Material", value: "Premium ceramic with antibacterial coating" },
    { label: "Control", value: "Wireless remote + side panel" },
    { label: "Certifications", value: "cUPC, ETL, WaterSense" },
    { label: "Warranty", value: "3 years comprehensive" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/5">
      <SEO
        title="AURELUX M132 Smart Toilet | Instant-Heating Bidet"
        description="AURELUX M132 smart toilet: instant warm-water bidet with adjustable pressure, UV self-cleaning nozzles, heated seat memory and deodorization. CE certified."
        keywords="luxury smart toilet, AURELUX M132 bidet, premium instant heating toilet, wireless control smart toilet, luxury bathroom automation, premium wellness bidet, intelligent sanitary system, heated bidet seat, self-cleaning toilet nozzles, automatic deodorizer, UV sterilization toilet, user memory toilet, eco mode toilet, heated toilet seat control, bidet functionality, self-cleaning nozzle system, water saving toilet technology"
        url="/products/aurelius"
        image="https://www.aureum7trading.com/images/products/aurelius-series-product-main.jpg"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "AURELUX M132", url: "/products/aurelius" }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "AURELUX M132 Smart Toilet",
          "description": "Refined comfort with cutting-edge smart toilet functions featuring instant warm water cleansing, automatic deodorization, and self-cleaning nozzles",
          "brand": {
            "@type": "Brand",
            "name": "Aureum VII Trading"
          },
          "image": [
            "https://www.aureum7trading.com/images/products/aurelius-series-product-main.jpg"
          ],
          "gtin13": "0795479810013",
          "manufacturer": {
            "@type": "Organization",
            "name": "Aureum VII Trading"
          },
          "sku": "AURELUX-M132",
          "mpn": "M132",
          "gtin": "AVT-AURELIUS-013",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR",
            "price": 600,
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "url": "https://www.aureum7trading.com/products/aurelius",
            "seller": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "url": "https://www.aureum7trading.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "EUR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 3,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 10,
                  "maxValue": 40,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "@id": "https://www.aureum7trading.com/return-policy",
              "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn",
              "itemCondition": "https://schema.org/NewCondition",
              "refundType": "https://schema.org/FullRefund"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Dimensions",
              "value": "685 × 380 × 525 mm"
            },
            {
              "@type": "PropertyValue",
              "name": "Power Consumption",
              "value": "1340W"
            },
            {
              "@type": "PropertyValue",
              "name": "Water Pressure",
              "value": "0.08-0.75 MPa"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "3 years comprehensive"
            }
          ]
        }}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="text-sm font-medium">
                AVT Collection
              </Badge>
              <Badge variant="outline" className="text-sm">
                Model M2
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-6">
              AURELUX
              <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                Model M132
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
              The AURELUX M132 luxury smart toilet delivers refined comfort through cutting-edge intelligent technology. This premium bidet system combines instant warm water heating, automatic deodorization, and advanced hygiene features. Crafted for discerning homeowners, our luxury smart toilet seamlessly integrates wellness and technology to elevate your daily bathroom experience to unprecedented levels of sophistication.
            </p>

            <PriceFrom price={600} />

            {/* Features List */}
            <div className="max-w-3xl mx-auto mb-8 space-y-3 text-left">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Instant warm water cleansing</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Automatic deodorization</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Self-cleaning nozzles</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Remote control operation</span>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {["AURELIUS", "Smart Toilet", "Model M2", "AVT Collection", "Luxury Bathroom"].map((tag) => (
                <Badge key={tag} variant="outline" className="text-sm">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {product && (
            <div className="max-w-4xl mx-auto space-y-8">
              <ProductMetadata 
                product={product} 
                showInternalCodes={false}
                className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6"
              />
              
              {/* PDF Download Button */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <PDFDownloadButton productName="AURELUX" />
                <Button variant="outline" asChild>
                  <a href="/smart-toilets">← Back to Collections</a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Product Gallery
          </h2>
          
          <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Product Demo Video */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See AURELIUS in Action
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the premium features and wider comfort design of the AURELIUS Series smart toilet
            </p>
          </div>
          
          <div className="relative max-w-4xl mx-auto">
            <OptimizedVideo
              src="/videos/aurelius-demo.mp4"
              poster={aureliusMainImage}
              controls
              className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-card"
              aria-label="AURELIUS smart toilet demonstration video"
            />
          </div>
        </div>
      </section>

      {/* Premium Smart Features */}
      <section className="py-16 bg-secondary/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Premium Smart Features
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Advanced technology meets comfort in the AURELIUS, featuring intelligent user memory and premium automation
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {premiumFeatures.map((feature, index) => (
              <article key={index} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30 rounded-lg bg-card p-6 flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Complete Luxury Experience */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Complete Luxury Experience
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-card/50 hover:bg-card transition-colors">
                <Sparkles className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications & Downloads */}
      <section className="py-16 bg-secondary/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Technical Specifications & Downloads
          </h2>
          
          <Tabs defaultValue="specifications" className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="installation">Installation</TabsTrigger>
              <TabsTrigger value="downloads">Downloads</TabsTrigger>
            </TabsList>
            
            <TabsContent value="specifications" className="mt-8">
              <div className="rounded-lg border bg-card shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-border/50 last:border-b-0">
                      <span className="font-medium text-sm">{spec.label}</span>
                      <span className="text-sm text-muted-foreground text-right">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="installation" className="mt-8">
              <div className="rounded-lg border bg-card shadow-sm p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Installation Requirements
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Electrical: Dedicated 15A circuit with GFCI protection</li>
                    <li>• Plumbing: Standard rough-in 300mm (adjustable 200-450mm)</li>
                    <li>• Space: Minimum 760mm × 460mm floor space</li>
                    <li>• Water pressure: 0.08-0.75 MPa (12-109 PSI)</li>
                    <li>• Professional installation recommended</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Plumbing Guidelines
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    The AURELIUS requires standard 3" waste outlet connection and 1/2" water supply. 
                    Ensure adequate clearance for service access and proper ventilation for electronic components.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="downloads" className="mt-8">
              <div className="grid grid-cols-1 gap-6">
                {/* Enhanced PDF Download Card */}
                <PDFDownloadButton 
                  productName="AURELIUS"
                  variant="card"
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-lg border bg-card shadow-sm p-6">
                    <h3 className="text-lg font-semibold mb-4">Additional Resources</h3>
                    <div className="space-y-3">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => handleDownload('RRL_CompanyProfile.pdf', 'Installation-Guide.pdf')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Installation Guide
                      </Button>
                    </div>
                  </div>
                
                  <div className="rounded-lg border bg-card shadow-sm p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      Quality Guarantee
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• 3-year comprehensive warranty</li>
                      <li>• cUPC, ETL, WaterSense certified</li>
                      <li>• Premium ceramic construction</li>
                      <li>• Professional support network</li>
                      <li>• Antibacterial coating standard</li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* Enhanced Call to Action */}
      <section className="py-20 bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience AURELIUS?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Transform your bathroom with the perfect blend of comfort, technology, and premium design
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="min-w-[200px] bg-primary hover:bg-primary/90">
              <a href="mailto:info@aureum7trading.com?subject=AURELIUS%20M132%20Quote%20Request">
                <Phone className="h-5 w-5 mr-2" />
                Get Quote
              </a>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="min-w-[200px]"
              onClick={() => handleDownload('RRL_Catalog.pdf', 'AURELIUS-Brochure.pdf')}
            >
              <Download className="h-5 w-5 mr-2" />
              Download Brochure
            </Button>
            <Button variant="secondary" size="lg" asChild className="min-w-[200px]">
              <a href="https://wa.me/34642278939?text=I'm%20interested%20in%20AURELIUS%20M132" target="_blank" rel="noopener noreferrer">
                <Mail className="h-5 w-5 mr-2" />
                Contact Sales
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="AURELUX" />

      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 rounded-full p-3 shadow-lg"
          size="icon"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};

export default Aurelius;